/**
 * The organisation's data page (`/t/$tenantId/dados`), for tenant admins only: how long a
 * campaign's data is kept after it ends, how long a post no live campaign reads as related
 * is kept after it was collected, and the data-processing record both belong to. `api`
 * serves the setting to a tenant-wide admin and nobody else.
 */
import { useId, useState, type ReactNode } from 'react';
import { useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { z } from 'zod';

import type { ApiFailureError } from '../../../lib/http/errors';
import { DurationInput, durationInRange, formatDuration } from '../../../chrome/form';
import { Page, PageHeader, PageSection } from '../../../chrome/page';
import { ErrorState } from '../../../chrome/state';
import { QueryStates } from '../../../chrome/state/QueryStates';
import { Button } from '../../../ui/button';
import { Field, FieldDescription, FieldLabel } from '../../../ui/field';
import {
  MAX_RETENTION_SECONDS,
  MIN_RETENTION_SECONDS,
  MIN_UNRELATED_RETENTION_SECONDS,
} from '../../campaign/record/api/schemas';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';
import { useApiClient } from '../../session/client';
import { dataRetentionSchema } from '../api/schemas';
import { dt } from './messages';

type DataRetention = z.infer<typeof dataRetentionSchema>;

const RETENTION_QUERY_KEY = (tenantId: string) => ['tenant', tenantId, 'data-retention'] as const;

function useDataRetention(tenantId: string): UseQueryResult<DataRetention, ApiFailureError> {
  const client = useApiClient();
  return useQuery<DataRetention, ApiFailureError>({
    queryKey: RETENTION_QUERY_KEY(tenantId),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.dataRetention, { tenantId }),
        GET_ROUTES.dataRetention.schema,
      ),
  });
}

function RetentionForm(props: { tenantId: string; saved: DataRetention }): ReactNode {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [campaignSeconds, setCampaignSeconds] = useState<number | null>(
    props.saved.campaign_seconds,
  );
  const [unrelatedSeconds, setUnrelatedSeconds] = useState<number | null>(
    props.saved.unrelated_media_seconds,
  );
  const mutation = useMutation<DataRetention, ApiFailureError, DataRetention>({
    mutationFn: (body) =>
      client.putParsed(`/tenants/${props.tenantId}/data-retention`, body, dataRetentionSchema),
    onSuccess: (saved) => queryClient.setQueryData(RETENTION_QUERY_KEY(props.tenantId), saved),
  });
  const campaignId = useId();
  const unrelatedId = useId();
  const valid =
    durationInRange(campaignSeconds, MIN_RETENTION_SECONDS, MAX_RETENTION_SECONDS) &&
    durationInRange(unrelatedSeconds, MIN_UNRELATED_RETENTION_SECONDS, MAX_RETENTION_SECONDS);
  const changed =
    campaignSeconds !== props.saved.campaign_seconds ||
    unrelatedSeconds !== props.saved.unrelated_media_seconds;

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && campaignSeconds !== null && unrelatedSeconds !== null) {
          mutation.mutate({
            campaign_seconds: campaignSeconds,
            unrelated_media_seconds: unrelatedSeconds,
          });
        }
      }}
    >
      <Field>
        <FieldLabel htmlFor={campaignId}>{dt('retention.label')}</FieldLabel>
        <DurationInput
          id={campaignId}
          seconds={campaignSeconds}
          onChange={setCampaignSeconds}
          minSeconds={MIN_RETENTION_SECONDS}
          maxSeconds={MAX_RETENTION_SECONDS}
        />
        <FieldDescription>{dt('retention.description')}</FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor={unrelatedId}>{dt('unrelated.label')}</FieldLabel>
        <DurationInput
          id={unrelatedId}
          seconds={unrelatedSeconds}
          onChange={setUnrelatedSeconds}
          minSeconds={MIN_UNRELATED_RETENTION_SECONDS}
          maxSeconds={MAX_RETENTION_SECONDS}
        />
        <FieldDescription>{dt('unrelated.description')}</FieldDescription>
      </Field>
      {mutation.error !== null && <ErrorState failure={mutation.error.failure} />}
      <div>
        <Button type="submit" disabled={!valid || !changed || mutation.isPending}>
          {dt('retention.save')}
        </Button>
      </div>
    </form>
  );
}

export function DataSettings(): ReactNode {
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const query = useDataRetention(tenantId);

  return (
    <Page>
      <PageHeader title={dt('heading')} />
      <QueryStates query={query} empty={null}>
        {(retention) => (
          <>
            <PageSection title={dt('retention.title')}>
              <RetentionForm
                key={`${retention.campaign_seconds}-${retention.unrelated_media_seconds}`}
                tenantId={tenantId}
                saved={retention}
              />
            </PageSection>
            <PageSection title={dt('processing.title')}>
              <p className="text-body-sm">
                {dt('processing.body', {
                  retention: formatDuration(retention.campaign_seconds),
                  unrelated: formatDuration(retention.unrelated_media_seconds),
                })}
              </p>
            </PageSection>
          </>
        )}
      </QueryStates>
    </Page>
  );
}
